/**
 * A6 Sub-Phase 4 — Frontend Sync Engine.
 *
 * Dequeues pending writes from offlineQueueDB and sends them to the backend.
 * Only runs when this tab is the leader (per queueLeader.getIsLeader()).
 *
 * Triggers:
 *   1. window 'online' event (primary)
 *   2. explicit syncNow() call (UI button, etc.)
 *   3. SW background-sync (stub — iOS Safari has no support, so we don't
 *      pretend; logged but no-op for now)
 *
 * Failure handling (per OFFLINE_PATTERNS.md Section 9 + A6-16):
 *   - 2xx                  -> markCompleted
 *   - 4xx (except 422)     -> markConflict (Reject-and-surface)
 *   - 422 idempotency_conflict -> markConflict (client bug, never retry)
 *   - 5xx, network error   -> markFailed (stays in PENDING, retryCount++)
 *   - retryCount >= 10     -> escalate to markConflict (give up retry)
 *
 * Concurrency safety: leader-check + atomic-claim dequeue (Step 3 design).
 * Even if multiple tabs accidentally race, the IndexedDB atomic-claim
 * ensures each entry is processed exactly once.
 */

import {
  dequeue,
  markCompleted,
  markFailed,
  markConflict,
  getCurrentScope,
  mapUuid,
  resolveUuid,
  resetFailedToPending,
} from './offlineQueueDB.js';
import { getIsLeader } from './queueLeader.js';

const MAX_RETRIES = 10;
const SYNC_DELAY_MS = 100; // brief pause between entries to avoid hammering server

// Module-level state
let syncInProgress = false;
let onlineListener = null;
let onSyncCycleStartCallback = null;
let onSyncCycleEndCallback = null;
let onEntryProcessedCallback = null;

/**
 * Extract the server-assigned ID from a successful response body.
 * Backend convention (verified in farm/router.py + fieldwork/router.py):
 *   responses to POST /resource have top-level `id` field as string.
 *
 * @param {object | null} responseBody
 * @returns {string | null}
 */
function extractServerId(responseBody) {
  if (!responseBody || typeof responseBody !== 'object') return null;
  if (typeof responseBody.id === 'string' && responseBody.id.length > 0) {
    return responseBody.id;
  }
  return null;
}

/**
 * Rewrite client-UUIDs in entry.body to server-IDs based on the UUID mapping
 * table. Mutates a deep copy — does NOT modify the original entry.
 *
 * @param {object} entry - Queue entry with optional clientRefs metadata
 * @returns {Promise<{body: object|null, unresolved: Array<{path, clientUuid}>}>}
 *   - body: rewritten body (or original if no refs)
 *   - unresolved: list of refs that could not be resolved (Field not yet synced)
 */
async function rewriteReferences(entry) {
  if (!entry.clientRefs || !Array.isArray(entry.clientRefs) || entry.clientRefs.length === 0) {
    return { body: entry.body, unresolved: [] };
  }

  // Deep clone body to avoid mutating queue entry
  const newBody = entry.body ? JSON.parse(JSON.stringify(entry.body)) : null;
  const unresolved = [];

  for (const ref of entry.clientRefs) {
    if (!ref.path || typeof ref.path !== 'string') continue;

    // Read current value at path (supports simple dot-notation, e.g., 'field_id' or 'parent.child_id')
    const pathParts = ref.path.split('.');
    let parent = newBody;
    for (let i = 0; i < pathParts.length - 1; i += 1) {
      if (parent == null || typeof parent !== 'object') {
        parent = null;
        break;
      }
      parent = parent[pathParts[i]];
    }
    if (parent == null || typeof parent !== 'object') continue;

    const leafKey = pathParts[pathParts.length - 1];
    const currentValue = parent[leafKey];
    if (typeof currentValue !== 'string' || currentValue.length === 0) continue;

    // Resolve: is this a client-UUID we know?
    const serverId = await resolveUuid(currentValue);
    if (serverId !== null) {
      parent[leafKey] = serverId;
    } else {
      // Could not resolve — this ref is unresolved (referenced entity not yet synced)
      unresolved.push({ path: ref.path, clientUuid: currentValue });
    }
  }

  return { body: newBody, unresolved };
}

/**
 * Reconstruct a FormData object from a persisted multipart entry.
 * Used by sendEntry() to replay offline FormData uploads.
 *
 * @param {Object} entry - QueueEntry with bodyEncoding='multipart'
 * @returns {FormData} A fresh FormData with all form fields + blob attachments
 *
 * A6 Sub-Phase 6b: enables offline file uploads to be replayed at sync time.
 */
function reconstructFormData(entry) {
  const fd = new FormData();
  if (entry.body && typeof entry.body === 'object') {
    for (const [fieldName, value] of Object.entries(entry.body)) {
      fd.append(fieldName, value);
    }
  }
  if (Array.isArray(entry.blobs)) {
    for (const att of entry.blobs) {
      if (att && att.blob instanceof Blob && typeof att.fieldName === 'string') {
        fd.append(att.fieldName, att.blob, att.filename || undefined);
      }
    }
  }
  return fd;
}

/**
 * Send a single queue entry to the backend.
 *
 * Returns the response category for the entry: 'success' | 'conflict' | 'retry'.
 * Does NOT modify the queue — caller (runSyncCycle) handles state transitions.
 */
async function sendEntry(entry) {
  const token = localStorage.getItem('aif_token');
  if (!token) {
    return { category: 'conflict', detail: { reason: 'no_auth_token', entry } };
  }

  const url = entry.url;
  const method = entry.method;

  // A6 Sub-Phase 4 Session 3: rewrite client-UUIDs to server-IDs before send
  const { body: rewrittenBody, unresolved } = await rewriteReferences(entry);
  if (unresolved.length > 0) {
    // Referenced entity not yet synced — block this entry, retry later
    return {
      category: 'retry',
      detail: {
        reason: 'unresolved_references',
        unresolved,
      },
    };
  }

  // A6 Sub-Phase 5: replay optimistic-concurrency headers for queued 3WM writes
  const ccHeaders = {};
  let sendBody = rewrittenBody;
  if ((method === 'PATCH' || method === 'DELETE') && entry.knownUpdatedAt) {
    ccHeaders['If-Unmodified-Since'] = entry.knownUpdatedAt;
    ccHeaders['X-Client-Edit-Time'] = entry.editTime
      || (entry.createdAt ? new Date(entry.createdAt).toISOString() : new Date().toISOString());
    ccHeaders['X-Client-Sync-Time'] = new Date().toISOString();
    if (entry.originalState && typeof entry.originalState === 'object') {
      sendBody = { ...(rewrittenBody || {}), _base: entry.originalState };
    }
  }

  // A6 Sub-Phase 6b: branch on bodyEncoding
  // - 'multipart': reconstruct FormData, skip Content-Type (browser sets boundary), skip ccHeaders
  //                (multipart uploads are POSTs, not 3WM-PATCHes — concurrency tracking N/A)
  // - 'json' or undefined: existing path with JSON.stringify + Content-Type: application/json
  const isMultipart = entry.bodyEncoding === 'multipart';

  let fetchBody;
  let fetchHeaders;
  if (isMultipart) {
    fetchBody = reconstructFormData(entry);
    fetchHeaders = {
      Authorization: `Bearer ${token}`,
      'X-Idempotency-Key': entry.idempotencyKey,
      ...(entry.headers || {}),
    };
    // Defensive: strip Content-Type if caller persisted one — browser must set boundary
    delete fetchHeaders['Content-Type'];
  } else {
    fetchBody = sendBody !== null && sendBody !== undefined
      ? JSON.stringify(sendBody)
      : undefined;
    fetchHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Idempotency-Key': entry.idempotencyKey,
      ...ccHeaders,
      ...(entry.headers || {}),
    };
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: fetchHeaders,
      body: fetchBody,
    });
  } catch (networkErr) {
    return {
      category: 'retry',
      detail: { reason: 'network_error', error: networkErr.message },
    };
  }

  // Parse response body (best-effort)
  let responseBody = null;
  try {
    const text = await response.text();
    responseBody = text ? JSON.parse(text) : null;
  } catch { /* best-effort parse */ }

  const status = response.status;

  // 2xx — success
  if (status >= 200 && status < 300) {
    return { category: 'success', status, body: responseBody };
  }

  // 422 with idempotency_conflict code — client bug, never retry
  if (status === 422 && responseBody?.code === 'idempotency_conflict') {
    return {
      category: 'conflict',
      detail: { reason: 'idempotency_conflict', status, body: responseBody },
    };
  }

  // A6 Sub-Phase 5: optimistic-concurrency conflict (tiered)
  if (status === 409 && responseBody?.code === 'optimistic_concurrency_conflict') {
    const tier = responseBody.tier;
    const detail = {
      kind: 'concurrency',
      tier,
      conflicting_fields: responseBody.conflicting_fields || [],
      current_state: responseBody.current_state || null,
      client_state: entry.body || null,
      client_base: entry.originalState || null,
      drift_detected: !!responseBody.drift_detected,
      drift_seconds: responseBody.drift_seconds ?? null,
    };
    if (tier === 2) {
      // Clocks trusted — auto-resolve: force re-send without precondition
      return { category: 'concurrency_autoresolve', detail };
    }
    // tier 3 (drift) — escalate to user via CONFLICTS store + Drawer
    return { category: 'conflict', detail };
  }

  // Other 4xx — conflict (Reject-and-surface)
  if (status >= 400 && status < 500) {
    return {
      category: 'conflict',
      detail: { reason: 'client_error', status, body: responseBody },
    };
  }

  // 5xx — transient, retry
  return {
    category: 'retry',
    detail: { reason: 'server_error', status, body: responseBody },
  };
}

/**
 * Run one full sync cycle: dequeue + send + state-transition until queue empty.
 *
 * Returns summary: { processed, completed, failed, conflicted }.
 *
 * Idempotent: safe to call multiple times. If already running, returns immediately.
 * Only runs if this tab is the leader.
 */
export async function runSyncCycle() {
  if (syncInProgress) {
    return { skipped: true, reason: 'already_in_progress' };
  }
  if (!getIsLeader()) {
    return { skipped: true, reason: 'not_leader' };
  }
  const scope = getCurrentScope();
  if (!scope) {
    return { skipped: true, reason: 'no_scope' };
  }

  syncInProgress = true;
  const summary = { processed: 0, completed: 0, failed: 0, conflicted: 0 };

  if (onSyncCycleStartCallback) {
    try { onSyncCycleStartCallback(); } catch { /* callback error ignored */ }
  }

  try {
    // Reset previously-failed entries to 'pending' so they're eligible for
    // dequeue in this cycle. This is the retry mechanism: each new sync cycle
    // gives failed entries another chance.
    await resetFailedToPending(scope);

    // Drain loop
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const entry = await dequeue(scope);
      if (!entry) break; // queue exhausted

      summary.processed += 1;
      const result = await sendEntry(entry);

      if (result.category === 'success') {
        await markCompleted(entry.id, result.body);
        summary.completed += 1;
        // A6 Sub-Phase 4 Session 3: record UUID mapping for downstream entries
        const serverId = extractServerId(result.body);
        if (serverId && entry.id) {
          try {
            await mapUuid(entry.id, serverId, scope);
          } catch (mapErr) {
            // Mapping failure does not fail the sync (entry is already completed),
            // but downstream refs to this client UUID will fail to resolve.
            console.warn('[syncEngine] mapUuid failed for entry', entry.id, mapErr);
          }
        }
      } else if (result.category === 'concurrency_autoresolve') {
        // Tier 2: corrected-edit policy says client wins. Re-send as a force
        // write by clearing the concurrency precondition, then complete.
        const forced = { ...entry, knownUpdatedAt: null, originalState: null };
        const forcedResult = await sendEntry(forced);
        if (forcedResult.category === 'success') {
          await markCompleted(entry.id, forcedResult.body);
          summary.completed += 1;
          const sid = extractServerId(forcedResult.body);
          if (sid && entry.id) {
            try { await mapUuid(entry.id, sid, scope); } catch { /* non-fatal */ }
          }
        } else {
          await markConflict(entry.id, {
            ...result.detail, force_retry_failed: true, last: forcedResult.detail,
          });
          summary.conflicted += 1;
        }
      } else if (result.category === 'conflict') {
        await markConflict(entry.id, result.detail);
        summary.conflicted += 1;
      } else if (result.category === 'retry') {
        const retryCount = (entry.retryCount || 0) + 1;
        if (retryCount >= MAX_RETRIES) {
          await markConflict(entry.id, {
            reason: 'max_retries_exceeded',
            lastError: result.detail,
            retryCount,
          });
          summary.conflicted += 1;
        } else {
          await markFailed(entry.id, JSON.stringify(result.detail));
          summary.failed += 1;
        }
      }

      if (onEntryProcessedCallback) {
        try { onEntryProcessedCallback(entry, result); } catch { /* callback error ignored */ }
      }

      // Brief pause between entries
      if (SYNC_DELAY_MS > 0) {
        await new Promise(r => setTimeout(r, SYNC_DELAY_MS));
      }
    }
  } finally {
    syncInProgress = false;
    if (onSyncCycleEndCallback) {
      try { onSyncCycleEndCallback(summary); } catch { /* callback error ignored */ }
    }
  }

  return summary;
}

/**
 * Start the sync engine. Registers the online-event listener and runs an
 * initial sync cycle if currently online.
 *
 * @param {object} [opts]
 * @param {() => void} [opts.onSyncCycleStart]
 * @param {(summary) => void} [opts.onSyncCycleEnd]
 * @param {(entry, result) => void} [opts.onEntryProcessed]
 */
export function startSyncEngine(opts = {}) {
  onSyncCycleStartCallback = opts.onSyncCycleStart || null;
  onSyncCycleEndCallback = opts.onSyncCycleEnd || null;
  onEntryProcessedCallback = opts.onEntryProcessed || null;

  if (onlineListener) return; // already started

  onlineListener = () => {
    runSyncCycle().catch((err) => {
      console.error('[syncEngine] online-triggered sync failed:', err);
    });
  };

  window.addEventListener('online', onlineListener);

  // If currently online, kick off initial sync after leader election settles
  if (navigator.onLine !== false) {
    setTimeout(() => {
      runSyncCycle().catch((err) => {
        console.error('[syncEngine] initial sync failed:', err);
      });
    }, 500);
  }
}

/**
 * Stop the sync engine. Removes listeners. Idempotent.
 */
export function stopSyncEngine() {
  if (onlineListener) {
    window.removeEventListener('online', onlineListener);
    onlineListener = null;
  }
  onSyncCycleStartCallback = null;
  onSyncCycleEndCallback = null;
  onEntryProcessedCallback = null;
}

/**
 * Trigger an explicit sync (e.g., user "Synchronisieren" button).
 */
export async function syncNow() {
  return runSyncCycle();
}

// Test-only
export const __testHooks = {
  isInProgress: () => syncInProgress,
  reset: () => {
    syncInProgress = false;
    if (onlineListener) {
      window.removeEventListener('online', onlineListener);
      onlineListener = null;
    }
    onSyncCycleStartCallback = null;
    onSyncCycleEndCallback = null;
    onEntryProcessedCallback = null;
  },
  MAX_RETRIES,
  sendEntry,
};
